import React from "react";
import "../styles/ErrorBoundary.css";

const SUPPORT_EMAIL = "founders@controlseat.com";

const IGNORED_PATTERNS = [
  /ResizeObserver loop/i,
  /getComputedStyle/i,
  /^Script error\.?$/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to fetch dynamically imported/i,
  /NetworkError when attempting to fetch/i,
  /AbortError/i,
  /cancelled/i,
  /WebSocket is already in CLOSING/i,
];

function isBenignError(error) {
  const message = String(error?.message || "");
  const stack = String(error?.stack || "");
  return IGNORED_PATTERNS.some((re) => re.test(message) || re.test(stack));
}

function isChunkLoadError(error) {
  const msg = error?.message || "";
  return /Loading (CSS )?chunk \d+ failed/i.test(msg) || /Failed to fetch dynamically imported/i.test(msg);
}

function suppressCRAOverlay() {
  if (process.env.NODE_ENV !== "development") return;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeName === "IFRAME" && node.id?.includes("webpack-dev-server-client-overlay")) {
          node.style.display = "none";
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

const DEDUP_COOLDOWN_MS = 3000;

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false, isChunkError: false };
    this._lastErrorKey = null;
    this._lastErrorTime = 0;
  }

  _shouldShowError(error) {
    if (isBenignError(error)) return false;
    const key = error?.message || "unknown";
    const now = Date.now();
    if (key === this._lastErrorKey && now - this._lastErrorTime < DEDUP_COOLDOWN_MS) {
      return false;
    }
    this._lastErrorKey = key;
    this._lastErrorTime = now;
    return true;
  }

  componentDidMount() {
    suppressCRAOverlay();

    this._onError = (event) => {
      const error = event.error || new Error(event.message || "Unknown error");
      if (!this._shouldShowError(error)) return;
      event.preventDefault();
      this.setState({
        hasError: true,
        error,
        errorInfo: null,
        isChunkError: isChunkLoadError(error),
      });
    };

    this._onUnhandledRejection = (event) => {
      const reason = event.reason;
      const error = reason instanceof Error ? reason : new Error(String(reason || "Unhandled promise rejection"));
      if (!this._shouldShowError(error)) return;
      event.preventDefault();
      this.setState({
        hasError: true,
        error,
        errorInfo: null,
        isChunkError: isChunkLoadError(error),
      });
    };

    window.addEventListener("error", this._onError);
    window.addEventListener("unhandledrejection", this._onUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this._onError);
    window.removeEventListener("unhandledrejection", this._onUnhandledRejection);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
  }

  handleContinue = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false, isChunkError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReport = () => {
    const { error, errorInfo } = this.state;
    const errorMessage = error?.message || "Unknown error";
    const stack = error?.stack || "";
    const componentStack = errorInfo?.componentStack || "";

    const subject = "CS Gateway Error Report";
    const body = [
      "--- Error Report ---",
      "",
      `Error: ${errorMessage}`,
      "",
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `User Agent: ${navigator.userAgent}`,
      "",
      "--- Stack Trace ---",
      stack,
      "",
      "--- Component Stack ---",
      componentStack,
      "",
      "--- Additional Info ---",
      "(Please describe what you were doing when the error occurred)",
      "",
    ].join("\n");

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, "_blank");
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render() {
    const { hasError, error, errorInfo, showDetails, isChunkError } = this.state;

    const errorMessage = isChunkError
      ? "A new version may be available. Please reload the page."
      : error?.message || "An unexpected error occurred";
    const stack = error?.stack || "";
    const componentStack = errorInfo?.componentStack || "";
    const fullDetails = [stack, componentStack ? "\nComponent Stack:" + componentStack : ""].join("");

    return (
      <>
        {this.props.children}
        {hasError && (
          <div className="errorBoundaryOverlay">
            <div className="errorBoundaryDialog">
              <div className="errorBoundaryHeader">
                <div className="errorBoundaryIcon">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                </div>
                <h2 className="errorBoundaryTitle">
                  {isChunkError ? "Update available" : "Something went wrong"}
                </h2>
              </div>

              <div className="errorBoundaryBody">
                <p className="errorBoundaryMessage">{errorMessage}</p>

                {!isChunkError && (
                  <button
                    type="button"
                    className={`errorBoundaryDetailsToggle ${showDetails ? "isOpen" : ""}`}
                    onClick={this.toggleDetails}
                  >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                    </svg>
                    {showDetails ? "Hide details" : "Show details"}
                  </button>
                )}

                {showDetails && !isChunkError && (
                  <pre className="errorBoundaryStack">{fullDetails}</pre>
                )}
              </div>

              <div className="errorBoundaryActions">
                {!isChunkError && (
                  <button type="button" className="errorBoundaryBtn btnContinue" onClick={this.handleContinue}>
                    Try to continue
                  </button>
                )}
                {!isChunkError && (
                  <button type="button" className="errorBoundaryBtn btnReport" onClick={this.handleReport}>
                    Report issue
                  </button>
                )}
                <button type="button" className="errorBoundaryBtn btnReload" onClick={this.handleReload}>
                  Reload
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}
